import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const TYPES = ['in', 'out', 'adjustment'];
const LIMIT = 20;

export default function StockMovements({ setTitle }) {
  const [tab, setTab]         = useState('log');        // 'log' | 'summary'
  const [movements, setMovements] = useState([]);
  const [summary, setSummary]   = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [typeFilter, setTypeFilter] = useState('');
  const [loading, setLoading]   = useState(false);
  const [modal, setModal]       = useState(false);
  const [products, setProducts] = useState([]);
  const [form, setForm]         = useState({ product_id: '', type: 'adjustment', qty: '', notes: '' });
  const [saving, setSaving]     = useState(false);

  useEffect(() => { setTitle('Stock Movements'); }, [setTitle]);
  useEffect(() => {
    api.get('/products/all').then(r => setProducts(r.data || [])).catch(() => {});
  }, []);

  const loadLog = useCallback(async () => {
    if (tab !== 'log') return;
    setLoading(true);
    try {
      const { data } = await api.get(`/stock?type=${typeFilter}&page=${page}&limit=${LIMIT}`);
      setMovements(data.data || []); setTotal(data.total || 0);
    } catch { setMovements([]); }
    finally { setLoading(false); }
  }, [tab, typeFilter, page]);

  const loadSummary = useCallback(async () => {
    if (tab !== 'summary') return;
    setLoading(true);
    try {
      const { data } = await api.get('/stock/summary');
      setSummary(data || []);
    } catch { setSummary([]); }
    finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { loadLog(); }, [loadLog]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.product_id) return alert('Product is required');
    if (!form.qty || Number(form.qty) <= 0) return alert('Quantity must be positive');
    setSaving(true);
    try {
      await api.post('/stock', form);
      setModal(false);
      setForm({ product_id: '', type: 'adjustment', qty: '', notes: '' });
      loadLog(); loadSummary();
      api.get('/products/all').then(r => setProducts(r.data || [])).catch(() => {});
    } catch (err) {
      alert(err.response?.data?.error || 'Error saving movement');
    } finally { setSaving(false); }
  };

  const totalPages = Math.ceil(total / LIMIT);

  const typeClass = { in: 'qty-in', out: 'qty-out', adjustment: 'qty-adj' };

  return (
    <div className="page">
      <div className="page-header">
        <h1>Stock Movements</h1>
        <button className="btn btn-primary" onClick={() => setModal(true)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Add Movement
        </button>
      </div>

      <div className="card">
        <div className="tab-bar">
          <button className={`tab-btn ${tab === 'log' ? 'active' : ''}`} onClick={() => { setTab('log'); setPage(1); }}>Movement Log</button>
          <button className={`tab-btn ${tab === 'summary' ? 'active' : ''}`} onClick={() => setTab('summary')}>Stock Summary</button>
        </div>

        {tab === 'log' && (
          <>
            <div className="toolbar">
              <select className="form-control" style={{ width: 160 }} value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }}>
                <option value="">All Types</option>
                {TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
              </select>
              <span className="text-muted" style={{ fontSize: 12, marginLeft: 'auto' }}>{total} records</span>
            </div>

            <div className="table-wrap">
              {loading ? <div className="loading">Loading…</div> : (
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Product</th>
                      <th>Type</th>
                      <th className="text-right">Qty</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movements.length === 0 ? (
                      <tr><td colSpan={5} className="empty">No movements found</td></tr>
                    ) : movements.map(m => (
                      <tr key={m.id}>
                        <td className="text-muted">{m.created_at ? m.created_at.slice(0, 16).replace('T', ' ') : '—'}</td>
                        <td><strong>{m.product_name}</strong></td>
                        <td><span className={`badge badge-${m.type}`}>{m.type}</span></td>
                        <td className={`text-right ${typeClass[m.type]}`}>
                          {m.type === 'in' ? '+' : m.type === 'out' ? '-' : '±'}{m.qty}
                        </td>
                        <td className="text-muted">{m.notes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <span>Showing {Math.min((page - 1) * LIMIT + 1, total)}–{Math.min(page * LIMIT, total)} of {total}</span>
                <div className="pagination-btns">
                  <button className="page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
                  {Array.from({ length: Math.min(totalPages, 8) }, (_, i) => i + 1).map(p => (
                    <button key={p} className={`page-btn ${p === page ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>
                  ))}
                  <button className="page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
                </div>
              </div>
            )}
          </>
        )}

        {tab === 'summary' && (
          <div className="table-wrap">
            {loading ? <div className="loading">Loading…</div> : (
              <table>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>SKU</th>
                    <th className="text-right">Current Stock</th>
                    <th className="text-right">Total In</th>
                    <th className="text-right">Total Out</th>
                    <th className="text-right">Adjustments</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.length === 0 ? (
                    <tr><td colSpan={6} className="empty">No stock data</td></tr>
                  ) : summary.map(s => (
                    <tr key={s.id}>
                      <td><strong>{s.name}</strong></td>
                      <td className="font-mono text-muted">{s.sku || '—'}</td>
                      <td className="text-right">
                        <span className={`stock-badge ${Number(s.stock) < 10 ? 'stock-low' : 'stock-ok'}`}>{s.stock} {s.unit}</span>
                      </td>
                      <td className="text-right qty-in">+{s.total_in || 0}</td>
                      <td className="text-right qty-out">-{s.total_out || 0}</td>
                      <td className="text-right qty-adj">±{s.total_adj || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h2>Add Stock Movement</h2>
              <button className="modal-close" onClick={() => setModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid cols-1">
                <div className="form-group">
                  <label>Product *</label>
                  <select className="form-control" value={form.product_id} onChange={e => f('product_id', e.target.value)}>
                    <option value="">Select product…</option>
                    {products.map(p => <option key={p.id} value={p.id}>{p.name} (Stock: {p.stock})</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Movement Type *</label>
                  <select className="form-control" value={form.type} onChange={e => f('type', e.target.value)}>
                    <option value="in">Stock In (receive)</option>
                    <option value="out">Stock Out (dispatch)</option>
                    <option value="adjustment">Adjustment (correction)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Quantity *</label>
                  <input type="number" className="form-control" value={form.qty} onChange={e => f('qty', e.target.value)} placeholder="0" min="1" />
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <textarea className="form-control" value={form.notes} onChange={e => f('notes', e.target.value)} placeholder="Reason for movement…" />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save Movement'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
